/**
 * FAL.AI Service - Video and Image generation (now routes through User Backend)
 * 
 * This service maintains backward compatibility with existing components
 * while routing all AI requests through the secure User Backend.
 * 
 * NO API KEYS IN FRONTEND - all generation goes through:
 * Frontend → User Backend → Admin Backend → FAL.AI
 */

import * as aiService from './aiService';

// Legacy storage key - kept for backward compatibility but no longer used
export const FAL_API_KEY_STORAGE_KEY = 'loopai-adstudio-fal-api-key';

// ==================== VIDEO GENERATION ====================

export const generateVideoWithKling25Turbo = async (prompt, imageUrl, endImageUrl, aspectRatio, duration, onProgress) => {
    if (onProgress) onProgress('Submitting to Kling 2.5 Turbo via secure backend...');
    const result = await aiService.generateVideoWithKling(prompt, imageUrl, endImageUrl, aspectRatio, duration, onProgress);
    return result.videoUrl;
};

export const generateVideoWithKling26 = async (prompt, imageUrl, aspectRatio, duration, onProgress) => {
    if (onProgress) onProgress('Submitting to Kling 2.6 via secure backend...');
    const result = await aiService.generateVideoWithKling(prompt, imageUrl, null, aspectRatio, duration, onProgress);
    return result.videoUrl;
};

export const generateVideoWithSora2 = async (prompt, imageUrl, onProgress, resolution = '720p') => {
    if (onProgress) onProgress('Submitting to Sora 2 via secure backend...');
    const result = await aiService.generateVideoWithSora(prompt, imageUrl, onProgress, resolution);
    return result.videoUrl;
};

export const generateVideoWithLTXFast = async (prompt, imageUrl, onProgress, resolution = '720p') => {
    if (onProgress) onProgress('Submitting to LTX Video via secure backend...');
    const result = await aiService.generateVideoWithLTX(prompt, imageUrl, onProgress, resolution);
    return result.videoUrl;
};

export const generateVideoWithMinimax = async (prompt, imageUrl, onProgress) => {
    if (onProgress) onProgress('Submitting to Minimax via secure backend...');
    const result = await aiService.generateVideoWithMinimax(prompt, imageUrl, onProgress);
    return result.videoUrl;
};

export const generateVideoWithWan22Animate = async (prompt, imageUrl, onProgress, aspectRatio = '16:9', resolution = '480p') => {
    if (onProgress) onProgress('Submitting to Wan 2.2 via secure backend...');
    const result = await aiService.generateVideoWithWan(prompt, imageUrl, aspectRatio, resolution, onProgress);
    return result.videoUrl;
};

export const generateVideoWithWan22AnimateMove = async (prompt, imageUrl, referenceVideoUrl, onProgress, resolution = '480p') => {
    // Motion transfer - falls back to standard Wan animation
    if (onProgress) onProgress('Submitting to Wan 2.2 Motion via secure backend...');
    const result = await aiService.generateVideoWithWan(prompt, imageUrl, '16:9', resolution, onProgress);
    return result.videoUrl;
};

// ==================== IMAGE GENERATION ====================

export const editImageWithGPT15 = async (prompt, imageUrls, aspectRatio = '16:9') => {
    const result = await aiService.editImage(imageUrls[0], prompt, [], imageUrls.slice(1), null, 'gpt-image');
    return result.imageUrl;
};

export const generateImageWithSeedream = async (prompt, referenceImages, aspectRatio) => {
    const result = await aiService.generateImage(prompt, 1, 'seedream', aspectRatio, referenceImages);
    return result.images[0];
};

export const generateImageWithSeedreamTextToImage = async (prompt, aspectRatio) => {
    const result = await aiService.generateImage(prompt, 1, 'seedream', aspectRatio, []);
    return result.images[0];
};

export const generateImageWithFlux2 = async (prompt, aspectRatio) => {
    const result = await aiService.generateImage(prompt, 1, 'flux', aspectRatio, []);
    return result.images[0];
};

export const generateImageWithFlux2Ref = async (prompt, referenceImages, aspectRatio) => {
    const result = await aiService.generateImage(prompt, 1, 'flux-ref', aspectRatio, referenceImages);
    return result.images[0];
};

export const generateImageWithFlux2MaxRef = async (prompt, referenceImages, aspectRatio) => {
    return generateImageWithFlux2Ref(prompt, referenceImages, aspectRatio);
};

// ==================== LIPSYNC ====================

export const generateLipsyncWithPixverse = async (videoUrl, audioUrl, onProgress) => {
    if (onProgress) onProgress('Generating lipsync via secure backend...');
    const result = await aiService.generateLipsync(videoUrl, audioUrl, onProgress);
    return result.videoUrl;
};

// ==================== DEPRECATED / NO-OP FUNCTIONS ====================

// These functions are no longer needed as API keys are managed server-side
export const showApiKeyModal = (onKeyProvided) => {
    console.warn('API keys are now managed server-side. No action needed.');
    return null;
};

export const getFalApiKey = () => {
    console.warn('API keys are now managed server-side.');
    return 'managed-server-side';
};
